import type { RequestConfig } from '@/netadapters/AbsNetAdapter'
import { configWithUpload, HTTP_STATUS_OK } from '@/netadapters/AbsNetAdapter'
import type { AxiosRequestConfig, AxiosResponse } from 'axios'
import axios from 'axios'
import { DEFAULT_SITE_BASE, PATH_SEPARATOR } from '@core/Constants'
import { CloudAdapter } from '@/netadapters/CloudAdapter'
import { isEmpty } from '@core/Utils'
import type { IFileTransmissionProgressListener } from '@core/FileTransmissionProgressListener'
import type { HttpRequestCancelToken } from '@/core/HttpRequestCancelToken'
import { HttpLocalCancelException, HttpRemoteCancelException, UnrecoverableException } from '@/core/Exceptions'
import { AuthAdapter } from '@/netadapters/AuthAdapter'

export const STATUS_CODE_ACCESS_TOKEN_INVALID = 401

export const STATUS_CODE_NATIVE_SERVICE_BIND_FAILED = 500

export const AUTH_COOKIE_INVALID = 400001

const PATH_CALL_SERVICE = 'service/call'
const PATH_UPLOAD_SERVICE = 'file/upload'

export const PATH_PROBE_SERVICE = 'probe'
const REQUEST_PROTOCOL = 'https' // Default request schema，should always be
const CODE_SUCCEED = 'SUCCESS'
const HEADER_ACCESS_TOKEN = 'accesstoken'

export const OPEN_HTTPS_PORT_SERVERS_ID = '100001'

export const OPEN_HTTP_PORT_SERVERS_ID = '100002'

export interface EnablePortResponse {
  outParams: {
    port: number
    detectPort: number
  }
  serviceId: number
}

export interface RemoteServiceResponse {
  code: string
  statusCode: number
  message: string
  data: unknown
}

export class EndpointAdapter extends AuthAdapter<RemoteServiceResponse> {
  #accessToken: string | undefined
  #cloudAdapter = new CloudAdapter()
  #deviceId: string | undefined
  #enableRetry = true

  constructor(enableRetry = true) {
    super()

    this.#enableRetry = enableRetry

    this._initInstance()
  }

  protected _getBaseUrl(): string {
    return ''
  }

  protected _isSuccess(response: AxiosResponse<RemoteServiceResponse>, judgingOnlyByStatus?: boolean): boolean {
    if (judgingOnlyByStatus) return super._isSuccess(response)

    const isSuccess = super._isSuccess(response) && response.data.code === CODE_SUCCEED

    return response.data.statusCode ? isSuccess && response.data.statusCode === HTTP_STATUS_OK : isSuccess
  }

  /**
   * Assemble the request to invoke the service which specified by the id on the endpoint(device).
   *
   * @param baseUrl [URL] of the request base
   * @param serviceId the id of service
   * @param data the payload(params) to invoked service
   * @param config the request configuration, including timeout settings, etc.
   * @param requestCancelToken?? used to cancel request
   */
  async callService(
    baseUrl: URL,
    serviceId: string,
    data = {},
    config: RequestConfig,
    requestCancelToken?: HttpRequestCancelToken
  ) {
    const url = baseUrl

    url.protocol = REQUEST_PROTOCOL
    url.pathname = Array.of<string>(
      url.pathname === DEFAULT_SITE_BASE ? '' : url.pathname,
      PATH_CALL_SERVICE,
      serviceId
    ).join(PATH_SEPARATOR)

    const reqConfig: AxiosRequestConfig = {}

    if (config && config.timeout && config.timeout > 0) {
      reqConfig.timeout = config.timeout
    }

    if (requestCancelToken) {
      reqConfig.signal = requestCancelToken?.signal
    }

    const res = await this._instance.post<unknown, AxiosResponse<RemoteServiceResponse>>(
      url.toString(),
      data,
      reqConfig
    )

    // 不需要处理statusCode和code，callService全部放开给业务侧自己处理
    if (this._isSuccess(res, true)) return res

    return undefined
  }

  /**
   * 上传文件到端侧
   *
   * @param baseUrl [URL] of the request base
   * @param file 上传的文件
   * @param md5 文件的md5
   * @param path 手机端的地址
   * @param progressListener {@link IFileTransmissionProgressListener} 可选参数。当文件上传进度变化是会被调用
   * @param requestCancelToken used to cancel request
   */
  async uploadFile(
    baseUrl: URL,
    file: File,
    md5: string,
    path: string,
    progressListener?: IFileTransmissionProgressListener,
    requestCancelToken?: HttpRequestCancelToken
  ) {
    const url = baseUrl

    url.protocol = REQUEST_PROTOCOL
    url.pathname = Array.of<string>(url.pathname === DEFAULT_SITE_BASE ? '' : url.pathname, PATH_UPLOAD_SERVICE).join(
      PATH_SEPARATOR
    )

    const formData = new FormData()

    formData.append('file', file)
    formData.append('md5', md5)
    formData.append('path', path)

    try {
      console.log(`[Transmission]: Lan uploadFile start fileName: ${file.name} ${new Date().toLocaleString()}`)

      const res = await this._instance.post<unknown, AxiosResponse<RemoteServiceResponse>>(url.toString(), formData, {
        ...configWithUpload,
        onUploadProgress: (event: ProgressEvent) => {
          progressListener?.onProgressChanged(event.loaded, event.total)
        },
        signal: requestCancelToken?.signal,
      })

      if (this._isSuccess(res)) {
        progressListener?.onCompleted()

        return res.data
      }
      console.log(`[Transmission]: Lan uploadFile error fileName: ${file.name} ${new Date().toLocaleString()}`, res)
    } catch (error) {
      console.log(`[Transmission]: Lan uploadFile error fileName: ${file.name} ${new Date().toLocaleString()}`, error)

      if (error instanceof HttpLocalCancelException) {
        progressListener?.onCancelled(false)
        throw new UnrecoverableException()
      } else if (error instanceof HttpRemoteCancelException) {
        progressListener?.onCancelled(true)
        throw new UnrecoverableException()
      }

      return undefined
    }
  }

  /**
   * 健康检查
   * @param baseUrl [URL] of the request base
   */
  async isDevicesChannelReachable(baseUrl: URL, requestCancelToken?: HttpRequestCancelToken) {
    baseUrl.protocol = REQUEST_PROTOCOL

    const res = await this.checkNetConnection(baseUrl, requestCancelToken ?? undefined)

    return this._isSuccess(res)
  }

  async checkNetConnection(baseUrl: URL, requestCancelToken?: HttpRequestCancelToken) {
    const url = baseUrl

    url.pathname = Array.of<string>(url.pathname === DEFAULT_SITE_BASE ? '' : url.pathname, PATH_PROBE_SERVICE).join(
      PATH_SEPARATOR
    )
    // 网络探测原本设置的不会重试，所以超时时间设置长一点，减少因为偶尔的网络波动，页面loading的问题
    const reqConfig: AxiosRequestConfig = {}

    if (requestCancelToken) {
      reqConfig.signal = requestCancelToken?.signal
    }

    const res = await this._instance.post<unknown, AxiosResponse<RemoteServiceResponse>>(url.toString(), {}, reqConfig)

    return res
  }

  /**
   * Set the device access token.
   *
   * @internal
   * @param token
   */
  setAccessToken(token: string) {
    this.#accessToken = token
  }

  /**
   * @internal
   */
  get accessToken(): string | undefined {
    return this.#accessToken
  }

  setDeviceId(id: string) {
    if (!isEmpty(id)) {
      this.#deviceId = id
    }
  }

  protected _getWithCredentialsOption(): boolean {
    return true
  }

  protected _isRetryEnabled(): boolean {
    return this.#enableRetry
  }

  protected _interceptRequest(config: AxiosRequestConfig): AxiosRequestConfig {
    const superConfig = super._interceptRequest(config)

    if (this.#accessToken) {
      superConfig.headers = {
        ...superConfig.headers,
        [HEADER_ACCESS_TOKEN]: this.#accessToken,
      }
    }

    return superConfig
  }

  protected _isInvalidAuth(res: AxiosResponse<RemoteServiceResponse>): boolean {
    return res.data.statusCode !== undefined && [AUTH_COOKIE_INVALID].includes(res.data.statusCode)
  }

  protected async _interceptFulfillResponse(res: AxiosResponse): Promise<AxiosResponse> {
    const response = await super._interceptFulfillResponse(res)

    // TODO: implement retry (times and interval)
    if (response.data.statusCode === STATUS_CODE_ACCESS_TOKEN_INVALID) {
      const originalRequestConfig = response.config

      if (this.#deviceId) {
        try {
          const token = await this.#cloudAdapter.getDeviceAccessToken(this.#deviceId)

          if (token) {
            this.setAccessToken(token)
            originalRequestConfig.headers = {
              ...originalRequestConfig.headers,
              [HEADER_ACCESS_TOKEN]: token,
            }

            return await axios.request(originalRequestConfig)
          }
        } catch (e) {
          // TODO :do retry
        }
      }
    }

    return response
  }

  /**
   * 新开https端口
   * @param deviceId
   * @param url
   * @returns
   */
  async enableHttpsPort(
    deviceId: string,
    url: URL,
    requestCancelToken?: HttpRequestCancelToken
  ): Promise<number | undefined> {
    this.setDeviceId(deviceId)

    try {
      const reqConfig: AxiosRequestConfig = {}

      if (requestCancelToken) {
        reqConfig.signal = requestCancelToken?.signal
      }

      const res = await this.callService(
        url,
        OPEN_HTTPS_PORT_SERVERS_ID,
        {
          params: { webhash: Date.now() },
        },
        reqConfig
      )

      if (res) return (res.data.data as EnablePortResponse).outParams.port

      return undefined
    } catch (error) {
      return undefined
    }
  }
}

export const INSTANCE = new EndpointAdapter()
